/* global updateProcurementPlanItemUrl syncProcurementPlanItemExpensesUrl syncProcurementPlanItemExpenseablesUrl */
/* global deleteProcurementPlanItemUrl deleteProcurementPlanItemAttachmentUrl */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import ProcurementEditModal from '../../procurement_plan/ProcurementEditModal';
import ProcurementModal from '../../procurement_plan/ProcurementModal';
import ProcurementPrModal from '../../procurement_plan/ProcurementPrModal';
import { useProcurement } from '../context/ProcurementContext';

import PlanCalendar from './PlanCalendar';
import PlanGantt from './PlanGantt';
import PlanKanban from './PlanKanban';
import RegisterTable from './RegisterTable';
import SegmentedControl from './SegmentedControl';
import ViewSwitcher, { PLAN_VIEWS } from './ViewSwitcher';

import { modalOptions } from 'Helpers/modalOptions';
import { apiClient, isSetupGateError } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ToggleInput from 'CommonComponents/ToggleInput';

import { useCustomEvent } from 'Hooks/useGlobalDOMEvents';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import SearchIcon from 'Assets/icons/search.svg';
import SortIcon from 'Assets/icons/sort.svg';

const GROUP_BY = { CATEGORY: 'category', DEPARTMENT: 'department' };

/**
 * IB-700 — the redesigned Procurement Plan tab. Replaces the placeholder that stood here since
 * IB-699. New presentation only: the register, Gantt, Kanban, grouping/search and the +PR/+EA/+DC
 * raise actions all reuse the existing endpoints and modal flows (see the IB-700 Linear notes).
 */
const PlanTab = () => {
    const { settings, routes } = useProcurement();
    const currencySymbol = settings.currencySymbol;
    const toast = useToast();

    // Toolbar state.
    const [view, setView] = useState(PLAN_VIEWS.REGISTER);
    const [groupBy, setGroupBy] = useState(GROUP_BY.CATEGORY);
    const [highPriority, setHighPriority] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Register data (grouped payloads keyed by category/department) + independent pagination
    // per section, mirroring the legacy Plan tab (25/page). Both sections page independently.
    const [outstanding, setOutstanding] = useState({ data: [], loading: true });
    const [completed, setCompleted] = useState({ data: [], loading: true });
    const outstandingPagination = usePagination({ perPage: 25 });
    const completedPagination = usePagination({ perPage: 25 });

    // Board views (Kanban + Gantt) show the WHOLE pipeline, so they are fetched independently of the
    // two register pagers — never truncated to a single 25-item register page. `groups` keeps the
    // grouped payload (source of truth, consumed by the Gantt); `items` is its flattened form
    // (consumed by the Kanban).
    const [boardItems, setBoardItems] = useState({ groups: [], items: [], loading: true });

    // Procurement-plan meta (policies, categories, statuses, priorities, complexities, plan).
    const [meta, setMeta] = useState(null);
    const [syncEnabled, setSyncEnabled] = useState(true);

    // Modal plumbing (reuses the existing Edit / View / PR modals + their fetches).
    const [currentProcurement, setCurrentProcurement] = useState(null);
    const [expensesTree, setExpensesTree] = useState([]);
    const [existedIds, setExistedIds] = useState([]);
    const [expenseParams, setExpenseParams] = useState({ sort_by: 'name', order: 'asc' });
    const [isCreatePrMode, setIsCreatePrMode] = useState(false);
    const [loadingProcurement, setLoadingProcurement] = useState(false);
    const [creating, setCreating] = useState(false);

    const editModal = useModal();
    const viewModal = useModal();
    const prModal = useModal();

    const deleteModalRef = useRef();
    const syncConfirmModalRef = useRef();
    const [deleteModalElement, setDeleteModalElement] = useState(null);
    const [syncConfirmModalElement, setSyncConfirmModalElement] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [deleteCompleted, setDeleteCompleted] = useState(false);
    const [deleteFailed, setDeleteFailed] = useState(false);
    const [deleteFailedMsg, setDeleteFailedMsg] = useState('');
    const [confirmationText, setConfirmationText] = useState('');

    const emitRefetchDashboard = useCustomEvent({ eventName: 'REFETCH_PROCUREMENT_DASHBOARD' });

    // Send both register sections back to page 1. Called from every filter change (below) so the
    // page reset and the filter state update batch into a single re-render → a single refetch,
    // avoiding the stale-page double-fetch/flicker a separate reset effect would cause.
    const resetRegisterPages = () => {
        outstandingPagination.setPage(1);
        completedPagination.setPage(1);
    };

    // Debounce the predictive search box (~300ms) so the register filters live as the user types.
    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            resetRegisterPages();
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const commonParams = useMemo(
        () => ({ group_by: groupBy, high_priority: highPriority ? 1 : 0, search: search || undefined }),
        [groupBy, highPriority, search],
    );

    const groupKey = groupBy === GROUP_BY.DEPARTMENT ? 'department' : 'category';

    // Copy the paginated grouped-collection meta onto a section's pagination, and step back a
    // page if a delete emptied the current one (mirrors the legacy Plan tab's behaviour).
    const applyMeta = (pagination, data) => {
        const meta = data?.meta ?? {};
        pagination.setTotal(meta.total ?? 0);
        pagination.setLastPage(meta.last_page ?? 1);
        pagination.setFrom(meta.from ?? 0);
        pagination.setTo(meta.to ?? 0);
        if (pagination.page > 1 && (data?.data ?? []).length === 0) {
            pagination.setPage(pagination.page - 1);
        }
    };

    const fetchOutstanding = useCallback(() => {
        setOutstanding((prev) => ({ ...prev, loading: true }));
        apiClient
            .get(routes.fetchProcurementPlanItemsUrl, {
                params: { ...commonParams, status: 'outstanding', per_page: outstandingPagination.perPage, page: outstandingPagination.page },
            })
            .then(({ data }) => {
                setOutstanding({ data: data.data ?? [], loading: false });
                applyMeta(outstandingPagination, data);
            })
            .catch(() => setOutstanding({ data: [], loading: false }));
    }, [routes.fetchProcurementPlanItemsUrl, commonParams, outstandingPagination.page, outstandingPagination.perPage]);

    const fetchCompleted = useCallback(() => {
        setCompleted((prev) => ({ ...prev, loading: true }));
        apiClient
            .get(routes.fetchProcurementPlanItemsUrl, {
                params: { ...commonParams, status: 'completed', per_page: completedPagination.perPage, page: completedPagination.page },
            })
            .then(({ data }) => {
                setCompleted({ data: data.data ?? [], loading: false });
                applyMeta(completedPagination, data);
            })
            .catch(() => setCompleted({ data: [], loading: false }));
    }, [routes.fetchProcurementPlanItemsUrl, commonParams, completedPagination.page, completedPagination.perPage]);

    // Board views (Kanban + Gantt) need the COMPLETE item set across all stages, independent of the
    // two register pagers. The items.index endpoint caps per_page at 100 (validation: in:10,25,50,
    // 100), so page through until every item is collected. Status is omitted so a single pass covers
    // both outstanding and completed; the active grouping / High-priority / search filters still
    // apply. Groups (keyed by category|department) are merged across pages preserving order, so the
    // Gantt gets the grouped payload and the Kanban gets a flattened list.
    const fetchBoardItems = useCallback(async () => {
        setBoardItems((prev) => ({ ...prev, loading: true }));
        try {
            const groupOrder = [];
            const groupsByHeading = {};
            let page = 1;
            let lastPage = 1;
            do {
                const { data } = await apiClient.get(routes.fetchProcurementPlanItemsUrl, {
                    params: { ...commonParams, per_page: 100, page },
                });
                (data.data ?? []).forEach((group) => {
                    const heading = group[groupKey];
                    if (!groupsByHeading[heading]) {
                        groupsByHeading[heading] = { ...group, items: [] };
                        groupOrder.push(heading);
                    }
                    groupsByHeading[heading].items.push(...(group.items ?? []));
                });
                lastPage = data.meta?.last_page ?? 1;
                page += 1;
            } while (page <= lastPage);

            const groups = groupOrder.map((heading) => groupsByHeading[heading]);
            const items = groups.flatMap((group) => group.items);
            setBoardItems({ groups, items, loading: false });
        } catch (error) {
            setBoardItems({ groups: [], items: [], loading: false });
        }
    }, [routes.fetchProcurementPlanItemsUrl, commonParams, groupKey]);

    const isBoardView = view === PLAN_VIEWS.KANBAN || view === PLAN_VIEWS.GANTT || view === PLAN_VIEWS.CALENDAR;

    const refetchRegister = useCallback(() => {
        fetchOutstanding();
        fetchCompleted();
        if (isBoardView) {
            fetchBoardItems();
        }
    }, [fetchOutstanding, fetchCompleted, fetchBoardItems, isBoardView]);

    const fetchMeta = useCallback(() => {
        apiClient
            .get(routes.fetchProcurementPlanUrl)
            .then(({ data }) => {
                setMeta(data);
                // AC: "insert into project plan" defaults ON when the plan has no stored preference.
                setSyncEnabled(data?.plan?.sync_projects_enabled ?? true);
            })
            .catch(() => {});
    }, [routes.fetchProcurementPlanUrl]);

    const fetchExpenses = useCallback(() => {
        apiClient
            .get(routes.fetchExpensesUrl, { params: expenseParams })
            .then(({ data }) => {
                setExpensesTree(data.expensesTree);
                setExistedIds(data.excludedExpensesIds);
            })
            .catch(() => {});
    }, [routes.fetchExpensesUrl, expenseParams]);

    // Expense-tree sort controls used inside the reused Edit/PR modals.
    const setOrdering = (e, sort) => {
        e.preventDefault();
        const order = sort === expenseParams.sort_by ? (expenseParams.order === 'asc' ? 'desc' : 'asc') : 'asc';
        setExpenseParams((prev) => ({ ...prev, sort_by: sort, order }));
    };

    const arrow = (name) => {
        if (name === expenseParams.sort_by) {
            return expenseParams.order === 'asc' ? <ChevronDownIcon className="tw-w-2 tw-h-2" /> : <ChevronUpIcon className="tw-w-2 tw-h-2" />;
        }
        return <SortIcon className="tw-w-3 tw-h-3" />;
    };

    const fetchProcurement = useCallback(
        (id) => {
            if (!id) {
                return;
            }
            setLoadingProcurement(true);
            apiClient
                .get(routes.fetchProcurementPlanItemUrl.replace('ITEM_ID', id))
                .then(({ data }) => setCurrentProcurement(data))
                .catch(() => setCurrentProcurement(null))
                .finally(() => setLoadingProcurement(false));
        },
        [routes.fetchProcurementPlanItemUrl],
    );

    useEffect(() => {
        fetchMeta();
        setDeleteModalElement(new Modal(deleteModalRef.current, modalOptions(false)));
        setSyncConfirmModalElement(new Modal(syncConfirmModalRef.current, modalOptions(false)));
    }, []);

    // Re-fetch the expense tree on mount and whenever its sort changes (driven from the modals).
    useEffect(() => {
        fetchExpenses();
    }, [fetchExpenses]);

    // Fetch each section on mount and whenever its page/perPage or the shared filters change.
    useEffect(() => {
        fetchOutstanding();
    }, [fetchOutstanding]);

    useEffect(() => {
        fetchCompleted();
    }, [fetchCompleted]);

    // Only fetch the (potentially large) full board set when a board view (Kanban or Gantt) is
    // actually shown, and re-fetch it when the active filters change while it's open.
    useEffect(() => {
        if (isBoardView) {
            fetchBoardItems();
        }
    }, [isBoardView, fetchBoardItems]);

    useCustomEvent({ eventName: 'REFETCH_PROCUREMENT_DASHBOARD', onSignal: () => refetchRegister() });

    // Flatten the expenses tree into the "id -> false" selection map the Edit/PR modals expect.
    const expensesIds = useMemo(() => {
        if (!expensesTree || !expensesTree.length) {
            return {};
        }
        const selected = {};
        const walkParents = (parents) => {
            parents.forEach((parent) => {
                (parent.expenses || []).forEach((expense) => {
                    selected[expense.id] = false;
                });
                if (parent.type === 'template') {
                    const dividers = _.isArray(parent.dividers) ? parent.dividers : Object.values(parent.dividers);
                    walkParents(dividers);
                }
            });
        };
        const walk = (node) => {
            (node.expenses || []).forEach((expense) => {
                selected[expense.id] = false;
            });
            walkParents(_.isArray(node.packs) ? node.packs : Object.values(node.packs));
            walkParents(_.isArray(node.templates) ? node.templates : Object.values(node.templates));
        };
        expensesTree.forEach((department) => {
            Object.values(department.costcenters).forEach((cc) => {
                walk(cc);
                (_.isArray(cc.projects) ? cc.projects : Object.values(cc.projects)).forEach((project) => walk(project));
            });
        });
        return selected;
    }, [expensesTree]);

    // ── Handlers (mirror the legacy ProcurementPlanIndex flows) ──────────────────────────────
    const showEdit = (item) => {
        fetchProcurement(item?.id);
        setIsCreatePrMode(false);
        editModal.show();
    };

    const showView = (item) => {
        fetchProcurement(item?.id);
        viewModal.show();
    };

    const showPr = (item) => {
        fetchProcurement(item?.id);
        setIsCreatePrMode(true);
        prModal.show();
    };

    // + EA → navigate to the existing EA/PO screen (approved PR routes to create-PO, else direct PO).
    const createEa = (item) => {
        window.location.href = item.purchase_requisition_id
            ? routes.createPurchaseOrderUrl.replace('REQUISITION_ID', item.purchase_requisition_id)
            : routes.directPurchaseOrderUrl.replace('PROCUREMENT_PLAN_ID', item.plan_id).replace('ITEM_ID', item.id);
    };

    // + DC → navigate to the existing single-screen Direct Commitment page (IB-635).
    const createDc = (item) => {
        window.location.href = routes.directCommitmentUrl.replace('PROCUREMENT_PLAN_ID', item.plan_id).replace('ITEM_ID', item.id);
    };

    const confirmDelete = (item) => {
        setCurrentProcurement(item);
        setConfirmationText(
            item.completed ? $t('This is a completed procurement - are you sure you want to delete it?') : $t('Are you sure you want delete this procurement?'),
        );
        deleteModalElement.show();
    };

    const triggerDelete = () => {
        setDeleteInProgress(true);
        apiClient
            .delete(deleteProcurementPlanItemUrl.replace('ITEM_ID', currentProcurement.id))
            .then(() => {
                refetchRegister();
                fetchExpenses();
                setDeleteCompleted(true);
                setTimeout(() => emitRefetchDashboard(), 5000);
            })
            .catch((error) => {
                setDeleteFailedMsg(error?.response?.data?.message || $t('Could not be deleted'));
                setDeleteFailed(true);
            });
    };

    const addProcurement = () => {
        if (!meta?.plan?.id) {
            return;
        }
        setCreating(true);
        apiClient
            .post(routes.storeProcurementPlanItemUrl.replace('PROCUREMENT_PLAN_ID', meta.plan.id))
            .then(({ data }) => {
                refetchRegister();
                // Reuse the existing edit flow so the user can immediately fill in the new procurement.
                showEdit(data);
            })
            .catch((error) => {
                if (!isSetupGateError(error)) {
                    toast.error($t('Error creating Procurement'));
                }
            })
            .finally(() => setCreating(false));
    };

    const persistSync = (enabled) => {
        apiClient
            .put(routes.syncProcurementPlanProjectsUrl, { enabled })
            .then(() => toast.success($t('Projects synced successfully')))
            .catch(() => {
                setSyncEnabled(!enabled);
                toast.error($t('Error syncing Projects with Procurement Plan'));
            });
    };

    const onSyncToggle = (checked) => {
        if (!checked) {
            syncConfirmModalElement.show();
            return;
        }
        setSyncEnabled(true);
        persistSync(true);
    };

    const onModalSuccess = () => {
        refetchRegister();
        fetchExpenses();
    };

    const closeEditPr = () => {
        setIsCreatePrMode(false);
        setCurrentProcurement(null);
        editModal.hide();
        prModal.hide();
        viewModal.hide();
    };

    const modalSharedProps = {
        procurement: currentProcurement,
        policies: meta?.policies ?? [],
        categories: meta?.categories ?? [],
        priorities: meta?.priorities ?? [],
        complexities: meta?.complexities ?? [],
        statuses: meta?.statuses ?? [],
        expensesTree,
        expensesIds,
        existedIds,
        setOrdering,
        arrow,
        reSyncing: false,
        lockedExpenseIds: undefined,
        onCreated: onModalSuccess,
        saveUrl: typeof updateProcurementPlanItemUrl !== 'undefined' ? updateProcurementPlanItemUrl : undefined,
        storePRUrl: routes.storePRUrl,
        indexPRUrl: routes.indexPRUrl,
        syncExpensesUrl: typeof syncProcurementPlanItemExpensesUrl !== 'undefined' ? syncProcurementPlanItemExpensesUrl : undefined,
        syncExpenseablesUrl: typeof syncProcurementPlanItemExpenseablesUrl !== 'undefined' ? syncProcurementPlanItemExpenseablesUrl : undefined,
        downloadAttachmentUrl: routes.downloadProcurementPlanItemAttachmentUrl,
        deleteAttachmentUrl: typeof deleteProcurementPlanItemAttachmentUrl !== 'undefined' ? deleteProcurementPlanItemAttachmentUrl : undefined,
        fetched: !!currentProcurement && !loadingProcurement,
        loading: loadingProcurement,
        isCreatePrMode,
        setLoading: () => {},
        eventStartDate: settings.eventStartDate,
        eventEndDate: settings.eventEndDate,
        onClose: closeEditPr,
        onCancel: closeEditPr,
        onSuccess: onModalSuccess,
        onAttachmentDeleted: (id) => fetchProcurement(id),
        onExpensesSynced: (id) => {
            fetchProcurement(id);
            fetchExpenses();
            refetchRegister();
        },
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Toolbar */}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <ViewSwitcher activeView={view} onChange={setView} />
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                    {/* Grouping only affects the grouped views (Register + Gantt); the Kanban is
                        always the five approval-step columns, so the toggle is hidden there. */}
                    {(view === PLAN_VIEWS.REGISTER || view === PLAN_VIEWS.GANTT) && (
                        <SegmentedControl
                            label={$t('Group')}
                            value={groupBy}
                            onChange={(value) => {
                                setGroupBy(value);
                                resetRegisterPages();
                            }}
                            options={[
                                { value: GROUP_BY.CATEGORY, label: $t('Category') },
                                { value: GROUP_BY.DEPARTMENT, label: $t('Department') },
                            ]}
                        />
                    )}
                    <ToggleInput
                        noBottomSpacing
                        label={$t('High priority')}
                        checked={highPriority}
                        onChange={(e) => {
                            setHighPriority(e.target.checked);
                            resetRegisterPages();
                        }}
                    />
                </div>
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <ToggleInput
                        noBottomSpacing
                        label={$t('Insert procurement plan into project plan')}
                        checked={!!syncEnabled}
                        onChange={(e) => onSyncToggle(e.target.checked)}
                    />
                    <Button variant="primary" small onClick={addProcurement} loading={creating} disabled={!meta?.plan?.id}>
                        {$t('+ Add procurement')}
                    </Button>
                </div>
                <div className="tw-relative tw-w-64">
                    <SearchIcon className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-my-auto tw-ms-3 tw-h-3.5 tw-w-3.5 tw-text-charcoal-50" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={$t('Search procurements…')}
                        className="tw-w-full tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-py-2 tw-ps-9 tw-pe-3 tw-text-sm tw-text-charcoal placeholder:tw-text-charcoal-50 focus:tw-border-blue focus:tw-outline-none"
                    />
                </div>
            </div>

            {/* Views */}
            {view === PLAN_VIEWS.REGISTER && (
                <div className="tw-flex tw-flex-col tw-gap-5">
                    <RegisterTable
                        title={$t('Outstanding procurements')}
                        groups={outstanding.data}
                        groupKey={groupKey}
                        loading={outstanding.loading}
                        pagination={outstandingPagination}
                        emptyText={$t('No outstanding procurements in this plan.')}
                        currencySymbol={currencySymbol}
                        settings={settings}
                        completed={false}
                        onEdit={showEdit}
                        onView={showView}
                        onDelete={confirmDelete}
                        onCreatePR={showPr}
                        onCreateEA={createEa}
                        onCreateDC={createDc}
                    />
                    <RegisterTable
                        title={$t('Completed procurements')}
                        groups={completed.data}
                        groupKey={groupKey}
                        loading={completed.loading}
                        pagination={completedPagination}
                        emptyText={$t('No completed procurements in this plan.')}
                        currencySymbol={currencySymbol}
                        settings={settings}
                        completed
                        onEdit={showEdit}
                        onView={showView}
                        onDelete={confirmDelete}
                        onCreatePR={showPr}
                        onCreateEA={createEa}
                        onCreateDC={createDc}
                    />
                </div>
            )}

            {view === PLAN_VIEWS.GANTT && (
                <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
                    {boardItems.loading ? <LoadingSpinner /> : <PlanGantt groups={boardItems.groups} currencySymbol={currencySymbol} />}
                </div>
            )}

            {view === PLAN_VIEWS.KANBAN && (
                <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
                    {boardItems.loading ? <LoadingSpinner /> : <PlanKanban items={boardItems.items} currencySymbol={currencySymbol} onView={showView} />}
                </div>
            )}

            {view === PLAN_VIEWS.CALENDAR && (
                <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
                    {boardItems.loading ? <LoadingSpinner /> : <PlanCalendar items={boardItems.items} />}
                </div>
            )}

            {/* Reused modals */}
            <ProcurementEditModal
                id="face-edit-procurement-modal"
                modal={editModal}
                {...modalSharedProps}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
                onRefresh={(id) => fetchProcurement(id)}
            />
            <ProcurementPrModal
                id="face-procurement-pr-modal"
                modal={prModal}
                {...modalSharedProps}
                onEdit={() => setIsCreatePrMode(false)}
                onCancelEdit={() => setIsCreatePrMode(true)}
            />
            <ProcurementModal
                id="face-procurement-modal"
                modal={viewModal}
                procurement={currentProcurement}
                fetched={!!currentProcurement && !loadingProcurement}
                loading={loadingProcurement}
                onClose={closeEditPr}
                downloadAttachmentUrl={routes.downloadProcurementPlanItemAttachmentUrl}
            />

            <ConfirmationModal
                closeHandler={() => {}}
                successHandler={() => deleteModalElement && deleteModalElement.hide()}
                triggerAction={triggerDelete}
                cleanStates={() => {
                    setDeleteInProgress(false);
                    setDeleteFailed(false);
                    setDeleteCompleted(false);
                    setDeleteFailedMsg('');
                }}
                allowAction={true}
                modalId="face-delete-procurement"
                modalRef={deleteModalRef}
                modalElement={deleteModalElement}
                request={deleteInProgress}
                actionCompleted={deleteCompleted}
                actionFailed={deleteFailed}
                confirmationMsg={confirmationText}
                actionCompletedMsg={$t('Deleted')}
                actionFailedMsg={deleteFailedMsg || $t('Could not be deleted')}
                title={$t('Delete procurement')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Delete')}
            />

            <ConfirmationModal
                closeHandler={() => {}}
                triggerAction={() => {
                    setSyncEnabled(false);
                    persistSync(false);
                    syncConfirmModalElement && syncConfirmModalElement.hide();
                }}
                allowAction={true}
                modalId="face-confirm-sync-projects"
                modalRef={syncConfirmModalRef}
                modalElement={syncConfirmModalElement}
                confirmationMsg={$t(
                    'Disabling the procurement plan will remove the Procurements and any associated actions from the Project Plan, are you sure you want to proceed?',
                )}
                title={$t('Disable the procurement plan')}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('Disable')}
            />
        </div>
    );
};

export default PlanTab;
